import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Modal, Row, Col, Button, DatePicker, Select, Input, Radio } from "antd";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import _get from "lodash/get";
import moment from "moment";
import {
  createVoucherRequest,
  updateVoucherRequest,
} from "providers/FonehouseVoucher/slice";

const { TextArea } = Input;
const { Option } = Select;

const validationSchema = Yup.object().shape({
  title: Yup.string().required("Vui lòng nhập tiêu đề"),
  code: Yup.string().required("Vui lòng nhập mã voucher"),
  discountValue: Yup.number()
    .required("Vui lòng nhập giá trị giảm")
    .min(1, "Giá trị phải lớn hơn 0"),
  startDate: Yup.date().required("Vui lòng chọn ngày bắt đầu").nullable(),
  endDate: Yup.date().required("Vui lòng chọn ngày kết thúc").nullable(),
});


const generateRandomCode = (length = 6) => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let code = "";
  for (let i = 0; i < length; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

const CreateVoucherModal = ({ visible, onClose, editData }) => {
  const dispatch = useDispatch();
  const isCreating = useSelector((state) =>
    _get(state, "voucherFonehouse.isCreating", false)
  );

  const isEdit = !!editData;
  const [codeMode, setCodeMode] = useState("auto");
  const [autoCode, setAutoCode] = useState("");

  useEffect(() => {
    if (!visible) {
      setCodeMode("auto");
      setAutoCode("");
    }
  }, [visible]);

  useEffect(() => {
    if (isEdit && editData?.code) {
      setCodeMode("manual");
    } else if (visible && !isEdit) {
      setAutoCode(generateRandomCode(6));
    }
  }, [isEdit, editData, visible]);

  const initialValues = {
    title: editData?.title || "",
    code: editData?.code || "",
    condition: editData?.condition || "",
    discountType: editData?.discountType || "FIXED_AMOUNT",
    discountValue: editData?.discountValue || "",
    maxDiscountAmount: editData?.maxDiscountAmount || 0,
    minOrderAmount: editData?.minOrderAmount || 0,
    currency: editData?.currency || "JPY",
    startDate: editData?.startDate ? moment(editData.startDate) : null,
    endDate: editData?.endDate ? moment(editData.endDate) : null,
    description: editData?.description || "",
    displayType: editData?.displayType || ["TEXT_CODE"],
  };

  const handleSubmit = (values) => {
    const payload = {
      ...values,
      displayType: ["TEXT_CODE"],
      startDate: values.startDate ? values.startDate.toISOString() : undefined,
      endDate: values.endDate ? values.endDate.toISOString() : undefined,
    };

    if (isEdit) {
      dispatch(updateVoucherRequest({ id: editData._id, ...payload }));
    } else {
      dispatch(createVoucherRequest(payload));
    }
  };

  const formikRef = useRef(null);

  const handleGenerateCode = () => {
    const newCode = generateRandomCode(6);
    setAutoCode(newCode);
    if (formikRef.current) {
      formikRef.current.setFieldValue("code", newCode);
    }
  };

  useEffect(() => {
    if (autoCode && formikRef.current && !isEdit) {
      formikRef.current.setFieldValue("code", autoCode);
    }
  }, [autoCode, isEdit]);

  return (
    <Modal
      title={isEdit ? "Sửa mã khuyến mãi" : "Tạo mã khuyến mãi"}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      destroyOnClose
    >
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, setFieldValue, handleChange, handleBlur }) => (
            <Form>
              <Row gutter={24}>
                {/* Left column */}
                <Col span={12}>
                  <div style={{ marginBottom: 16 }}>
                    <label>
                      Tiêu đề Voucher<span style={{ color: "red" }}>*</span>
                    </label>
                    <Input
                      name="title"
                      placeholder="Nhập tiêu đề"
                      value={values.title}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      status={touched.title && errors.title ? "error" : ""}
                    />
                    {touched.title && errors.title && (
                      <div style={{ color: "red", fontSize: 12 }}>{errors.title}</div>
                    )}
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <label>
                      Điều kiện khuyến mãi
                    </label>
                    <Input
                      name="condition"
                      placeholder="Nhập tên danh mục..."
                      value={values.condition}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <label>
                      Tỉ lệ khuyến mãi<span style={{ color: "red" }}>*</span>
                    </label>
                    <Row gutter={8}>
                      <Col span={12}>
                        <Input
                          name="discountValue"
                          placeholder="Giảm ¥1.000..."
                          type="number"
                          value={values.discountValue}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          status={touched.discountValue && errors.discountValue ? "error" : ""}
                        />
                      </Col>
                      <Col span={12}>
                        <Select
                          value={values.discountType}
                          onChange={(val) => setFieldValue("discountType", val)}
                          style={{ width: "100%" }}
                        >
                          <Option value="FIXED_AMOUNT">Yên</Option>
                          <Option value="PERCENTAGE">%</Option>
                        </Select>
                      </Col>
                    </Row>
                    {touched.discountValue && errors.discountValue && (
                      <div style={{ color: "red", fontSize: 12 }}>{errors.discountValue}</div>
                    )}
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <Row gutter={8}>
                      <Col span={12}>
                        <label>Ngày bắt đầu</label>
                        <DatePicker
                          format="DD/MM/YYYY"
                          value={values.startDate}
                          onChange={(date) => setFieldValue("startDate", date)}
                          style={{ width: "100%" }}
                          placeholder="dd/mm/yyyy"
                        />
                        {touched.startDate && errors.startDate && (
                          <div style={{ color: "red", fontSize: 12 }}>{errors.startDate}</div>
                        )}
                      </Col>
                      <Col span={12}>
                        <label>Ngày kết thúc</label>
                        <DatePicker
                          format="DD/MM/YYYY"
                          value={values.endDate}
                          onChange={(date) => setFieldValue("endDate", date)}
                          style={{ width: "100%" }}
                          placeholder="dd/mm/yyyy"
                        />
                        {touched.endDate && errors.endDate && (
                          <div style={{ color: "red", fontSize: 12 }}>{errors.endDate}</div>
                        )}
                      </Col>
                    </Row>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <label>Mã voucher<span style={{ color: "red" }}>*</span></label>
                    <div style={{ marginBottom: 8 }}>
                      <Radio.Group
                        value={codeMode}
                        onChange={(e) => {
                          setCodeMode(e.target.value);
                          if (e.target.value === "auto") {
                            handleGenerateCode();
                          } else {
                            setFieldValue("code", "");
                          }
                        }}
                        size="small"
                      >
                        <Radio.Button value="auto">Tạo tự động</Radio.Button>
                        <Radio.Button value="manual">Tự nhập</Radio.Button>
                      </Radio.Group>
                    </div>
                    {codeMode === "auto" ? (
                      <Input
                        value={values.code}
                        disabled
                        placeholder="Đang tạo mã..."
                        style={{ fontWeight: "bold", fontSize: 16, letterSpacing: 2 }}
                        suffix={
                          <Button type="link" size="small" onClick={handleGenerateCode} style={{ padding: 0 }}>
                            Đổi mã
                          </Button>
                        }
                      />
                    ) : (
                      <Input
                        name="code"
                        placeholder="Nhập mã voucher (VD: SALE2026)"
                        value={values.code}
                        onChange={(e) =>
                          setFieldValue("code", e.target.value.toUpperCase())
                        }
                        onBlur={handleBlur}
                        style={{ textTransform: "uppercase" }}
                        status={touched.code && errors.code ? "error" : ""}
                      />
                    )}
                    {touched.code && errors.code && (
                      <div style={{ color: "red", fontSize: 12 }}>{errors.code}</div>
                    )}
                  </div>
                </Col>

                {/* Right column */}
                <Col span={12}>
                  <div style={{ marginBottom: 16 }}>
                    <label>Nội dung Template</label>
                    <TextArea
                      name="description"
                      placeholder="Nhập nội dung"
                      rows={10}
                      maxLength={1000}
                      showCount
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>

                </Col>
              </Row>

              <Row justify="end" gutter={8} style={{ marginTop: 16 }}>
                <Col>
                  <Button onClick={onClose}>Huỷ</Button>
                </Col>
                <Col>
                  <Button type="primary" htmlType="submit" loading={isCreating}>
                    Xác nhận
                  </Button>
                </Col>
              </Row>
            </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default CreateVoucherModal;
